export type CompressOptions = {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0..1
  outputType?: 'image/jpeg' | 'image/webp';
};

function getTargetSize(
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number
) {
  let width = srcWidth;
  let height = srcHeight;
  if (width > maxWidth || height > maxHeight) {
    const widthRatio = maxWidth / width;
    const heightRatio = maxHeight / height;
    const ratio = Math.min(widthRatio, heightRatio);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  return { width, height };
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function compressImage(
  file: File,
  options: CompressOptions = {}
): Promise<File> {
  try {
    const { maxWidth = 1600, maxHeight = 1600, quality = 0.8, outputType } = options;

    // Skip non-images
    if (!file.type.startsWith('image/')) return file;

    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImage(dataUrl);

    const { width, height } = getTargetSize(img.naturalWidth || img.width, img.naturalHeight || img.height, maxWidth, maxHeight);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const mimeType: 'image/jpeg' | 'image/webp' = outputType || (file.type === 'image/webp' ? 'image/webp' : 'image/jpeg');

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), mimeType, quality)
    );
    if (!blob) return file;

    // If compression didn't help, keep original
    if (blob.size >= file.size) return file;

    const newName = (() => {
      const base = file.name.replace(/\.[^.]+$/, '');
      return `${base}.jpg`;
    })();

    return new File([blob], newName, { type: blob.type, lastModified: Date.now() });
  } catch {
    return file;
  }
}




